
import React, { useState, useEffect, useRef } from 'react';
import { X, ExternalLink, Clock } from 'lucide-react';
import { AdvertisingModalConfig } from '../types';

interface AdvertisingModalProps {
  isOpen: boolean;
  config: AdvertisingModalConfig;
  onClose: () => void;
}

const AdvertisingModal: React.FC<AdvertisingModalProps> = ({ isOpen, config, onClose }) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [canClose, setCanClose] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function to clear interval
  const clearCountdownInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset countdown when modal opens
      const timeout = config.timeoutSeconds || 5;
      setCountdown(timeout);
      setCanClose(timeout === 0);

      // Clear any existing interval
      clearCountdownInterval();

      if (timeout > 0) {
        intervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              setCanClose(true);
              clearCountdownInterval();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      // Reset state when modal closes
      clearCountdownInterval();
      setCountdown(0);
      setCanClose(false);
    }

    return clearCountdownInterval;
  }, [isOpen, config.timeoutSeconds]);

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  if (!isOpen || !config.enabled) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 transition-opacity duration-300 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Content */}
        <div
          className="relative bg-gradient-to-b from-zinc-900 to-black border-2 border-cyan-500/30 rounded-3xl max-w-2xl w-full shadow-[0_0_60px_rgba(6,182,212,0.4)] transform transition-all duration-300 ease-out"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative gradient lines */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent" />

          {/* Close Button */}
          <button
            onClick={handleClose}
            disabled={!canClose}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-colors z-10 ${canClose
                ? 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                : 'text-zinc-600 cursor-not-allowed'
              }`}
            title={canClose ? 'Cerrar' : `Espera ${countdown}s para cerrar`}
          >
            {!canClose ? (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-bold">{countdown}s</span>
              </div>
            ) : (
              <X className="w-5 h-5" />
            )}
          </button>

          {/* Content */}
          <div className="p-8 md:p-12">
            {/* Decorative accent */}
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-cyan-400 mb-6 text-center leading-tight animate-in slide-in-from-bottom-4 duration-500">
              {config.title}
            </h2>

            {/* Message */}
            <div className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 text-center max-w-xl mx-auto animate-in slide-in-from-bottom-6 duration-700 whitespace-pre-line">
              {config.message}
            </div>

            {/* Link (if provided) */}
            {config.linkText && config.linkUrl && (
              <div className="mb-8 text-center animate-in slide-in-from-bottom-8 duration-900">
                <a
                  href={config.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 font-bold py-4 px-10 rounded-xl transition-all duration-200 text-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white shadow-[0_0_30px_rgba(6,182,212,0.3)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] active:scale-95"
                >
                  {config.linkText}
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            )}

            {/* CTA Button */}
            <div className="text-center animate-in slide-in-from-bottom-10 duration-1000">
              <button
                onClick={handleClose}
                disabled={!canClose}
                className={`font-medium py-2 px-6 rounded-lg transition-all duration-200 text-sm ${canClose
                    ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-300'
                    : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
                  }`}
              >
                {canClose ? config.buttonText : `Espera ${countdown}s...`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvertisingModal;
